import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  Col,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import API_BASE_URL from '../../helpers/api/apiConfig';

const { Text } = Typography;

interface CompanyUsageTabProps {
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
  onAuthMismatch: (response: Response, errorData: any) => Promise<boolean>;
}

interface AreaTypeOption {
  id: number;
  name: string;
}

interface AreaTypeLimit {
  areaTypeIds: number[];
  maxCount: number;
}

interface AreaTypeLimitFormRow {
  areaTypeIds?: number[];
  maxCount?: number;
}

interface PlanFeatureFlags {
  genericAssistantEnabled?: boolean | null;
  lightboxIntegrationEnabled?: boolean | null;
  measurementAnalysisEnabled?: boolean | null;
  specialistAgronomicAiEnabled?: boolean | null;
  measurementLevelDetectionEnabled?: boolean | null;
  areaAnalysisEnabled?: boolean | null;
  entityAnalysisEnabled?: boolean | null;
  crossAreaAnalysisEnabled?: boolean | null;
}

interface SubscriptionPlan {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  sortOrder?: number | null;
  maxTotalUsers?: number | null;
  maxAdminUsers?: number | null;
  maxViewerUsers?: number | null;
  maxStandardUsers?: number | null;
  maxAreas?: number | null;
  areaTypeLimits?: AreaTypeLimit[] | null;
  maxEntities?: number | null;
  entityAreaTypeIds?: number[] | null;
  maxMeasurementsMonthly?: number | null;
  maxAiTokensMonthly?: number | null;
  trialDurationDays?: number | null;
  requiresLightbox?: boolean | null;
  allowMultiUser?: boolean | null;
  featureFlags?: PlanFeatureFlags | null;
}

interface CompanyLicense {
  id?: number | null;
  companyId?: number;
  company?: {
    id: number;
    name: string;
  };
  creatorUser?: {
    id: number;
    email: string;
  } | null;
  planCode?: string | null;
  status?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  billingCycle?: 'monthly' | 'annual' | null;
  maxTotalUsers?: number | null;
  maxAdminUsers?: number | null;
  maxViewerUsers?: number | null;
  maxStandardUsers?: number | null;
  maxAreas?: number | null;
  maxMeasurementsMonthly?: number | null;
  maxAiTokensMonthly?: number | null;
  hasLicense?: boolean;
}

interface CompanyUsageSnapshot {
  totalUsers: number;
  adminUsers: number;
  viewerUsers: number;
  standardUsers: number;
  areas: number;
  measurementsMonthly: number;
  aiTokensMonthly: number;
}

interface UsageWarning {
  key: string;
  message: string;
  current: number;
  limit: number;
}

const formatLimit = (limit?: number | null) => (
  typeof limit === 'number' ? limit.toString() : '∞'
);

const getCompanyId = (record: CompanyLicense): number | null => {
  const companyId = Number(record.company?.id || record.companyId);
  return Number.isFinite(companyId) && companyId > 0 ? companyId : null;
};

const getCompanyName = (record: CompanyLicense): string => {
  const companyId = getCompanyId(record);
  return record.company?.name || (companyId ? `Company ${companyId}` : 'Unknown company');
};

const hasAssignedLicense = (record: CompanyLicense): boolean => (
  record.hasLicense === true || (typeof record.id === 'number' && record.id > 0)
);

const normalizeAreaTypeIdList = (value: any): number[] | null => {
  if (!Array.isArray(value)) return null;

  const normalized = Array.from<number>(
    new Set<number>(
      value
        .map((id: any): number => Number(id))
        .filter((id: number): boolean => Number.isFinite(id) && id > 0)
    )
  ).sort((a, b) => a - b);

  return normalized.length > 0 ? normalized : null;
};

const resolveFeatureFlags = (plan?: SubscriptionPlan | null): Required<PlanFeatureFlags> => {
  const flags = (plan?.featureFlags && typeof plan.featureFlags === 'object')
    ? plan.featureFlags
    : {};
  const asBoolean = (value: any, fallback: boolean): boolean => (
    typeof value === 'boolean' ? value : fallback
  );

  const lightboxIntegrationEnabled = asBoolean(
    flags.lightboxIntegrationEnabled,
    asBoolean((flags as any).requiresLightbox, typeof plan?.requiresLightbox === 'boolean' ? plan.requiresLightbox : true)
  );

  const measurementAnalysisEnabled = asBoolean(
    flags.measurementAnalysisEnabled,
    asBoolean(
      flags.specialistAgronomicAiEnabled,
      asBoolean(flags.measurementLevelDetectionEnabled, true)
    )
  );

  const areaAnalysisEnabled = asBoolean(flags.areaAnalysisEnabled, true);
  const entityAnalysisEnabled = asBoolean(
    flags.entityAnalysisEnabled,
    asBoolean(flags.crossAreaAnalysisEnabled, true)
  );

  return {
    genericAssistantEnabled: true,
    lightboxIntegrationEnabled,
    measurementAnalysisEnabled,
    specialistAgronomicAiEnabled: measurementAnalysisEnabled,
    measurementLevelDetectionEnabled: measurementAnalysisEnabled,
    areaAnalysisEnabled,
    entityAnalysisEnabled,
    crossAreaAnalysisEnabled: entityAnalysisEnabled,
  };
};

const normalizeAreaTypeLimits = (limits: any): AreaTypeLimit[] | null => {
  if (!Array.isArray(limits)) return null;

  const normalized = limits
    .map((item) => {
      const maxCount = Number(item?.maxCount);
      if (!Number.isFinite(maxCount) || maxCount < 0) return null;

      // Backward compatibility with old single-ID shape.
      const rowIdsRaw: unknown[] = Array.isArray(item?.areaTypeIds)
        ? item.areaTypeIds
        : [item?.areaTypeId];
      const uniqueAreaTypeIds = new Set<number>(
        rowIdsRaw
          .map((id: unknown): number => Number(id))
          .filter((id: number): boolean => Number.isFinite(id) && id > 0)
      );
      const areaTypeIds = Array.from<number>(uniqueAreaTypeIds).sort((a, b) => a - b);

      if (areaTypeIds.length === 0) return null;

      return {
        areaTypeIds,
        maxCount: Math.floor(maxCount),
      } as AreaTypeLimit;
    })
    .filter(Boolean) as AreaTypeLimit[];

  if (normalized.length === 0) return null;
  return normalized;
};

const mapAreaTypeLimitsToFormRows = (limits?: AreaTypeLimit[] | null): AreaTypeLimitFormRow[] => {
  if (!Array.isArray(limits)) return [];

  return limits
    .map((limit) => {
      const maxCount = Number(limit?.maxCount);
      if (!Number.isFinite(maxCount) || maxCount < 0) return null;

      const areaTypeIdsRaw: unknown[] = Array.isArray(limit?.areaTypeIds)
        ? limit.areaTypeIds
        : [((limit as any)?.areaTypeId)];

      const uniqueAreaTypeIds = new Set<number>(
        areaTypeIdsRaw
          .map((id: unknown): number => Number(id))
          .filter((id: number): boolean => Number.isFinite(id) && id > 0)
      );
      const ids = Array.from<number>(uniqueAreaTypeIds).sort((a, b) => a - b);

      if (ids.length === 0) return null;

      return {
        areaTypeIds: ids,
        maxCount: Math.floor(maxCount),
      } as AreaTypeLimitFormRow;
    })
    .filter(Boolean) as AreaTypeLimitFormRow[];
};

const areaTypeLimitSummary = (
  limit: AreaTypeLimit,
  areaTypeNameById: Map<number, string>
): string => {
  const ids = Array.isArray(limit.areaTypeIds) && limit.areaTypeIds.length > 0
    ? limit.areaTypeIds
    : [((limit as any)?.areaTypeId)].filter((id) => Number.isFinite(Number(id)));

  const names = ids
    .map((id) => areaTypeNameById.get(id) || `AreaType ${id}`)
    .join(', ');

  return `${names}: max ${limit.maxCount} total`;
};

const CompanyUsageTab: React.FC<CompanyUsageTabProps> = ({
  buildAuthHeaders,
  onAuthMismatch,
}) => {
  const [areaTypes, setAreaTypes] = useState<AreaTypeOption[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [licenses, setLicenses] = useState<CompanyLicense[]>([]);
  const [usageByCompany, setUsageByCompany] = useState<Record<number, CompanyUsageSnapshot>>({});
  const [warningsByCompany, setWarningsByCompany] = useState<Record<number, UsageWarning[]>>({});

  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingLicenses, setLoadingLicenses] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);

  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignmentTarget, setAssignmentTarget] = useState<CompanyLicense | null>(null);

  const [planForm] = Form.useForm();
  const [assignForm] = Form.useForm();

  const areaTypeNameById = useMemo(() => {
    const map = new Map<number, string>();
    areaTypes.forEach((areaType) => map.set(areaType.id, areaType.name));
    return map;
  }, [areaTypes]);

  const fetchPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const response = await fetch(`${API_BASE_URL}/subscription-plans`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data?.data || []);
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to load subscription plans');
      }
    } catch (error) {
      console.error('Failed to load subscription plans', error);
      message.error('Failed to load subscription plans');
    } finally {
      setLoadingPlans(false);
    }
  }, [buildAuthHeaders, onAuthMismatch]);

  const fetchAreaTypes = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/area-types`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const items = Array.isArray(data?.data) ? data.data : [];
        setAreaTypes(
          items
            .map((item: any) => ({ id: Number(item.id), name: String(item.name || '') }))
            .filter((item: AreaTypeOption) => Number.isFinite(item.id) && item.id > 0 && item.name)
            .sort((a: AreaTypeOption, b: AreaTypeOption) => a.name.localeCompare(b.name))
        );
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
      }
    } catch (error) {
      console.error('Failed to load area types', error);
    }
  }, [buildAuthHeaders, onAuthMismatch]);

  const fetchLicenses = useCallback(async () => {
    setLoadingLicenses(true);
    try {
      const response = await fetch(`${API_BASE_URL}/company-licenses`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const items = data?.data || [];
        setLicenses(items);
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to load subscriptions');
      }
    } catch (error) {
      console.error('Failed to load subscriptions', error);
      message.error('Failed to load subscriptions');
    } finally {
      setLoadingLicenses(false);
    }
  }, [buildAuthHeaders, onAuthMismatch]);

  const fetchUsageForCompany = useCallback(async (companyId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/companies/${companyId}/usage`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const usage = data?.data?.usage;
        const warnings = data?.data?.warnings || [];
        if (usage) {
          setUsageByCompany((prev) => ({ ...prev, [companyId]: usage }));
        }
        setWarningsByCompany((prev) => ({ ...prev, [companyId]: warnings }));
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
      }
    } catch (error) {
      console.error('Failed to load usage snapshot', error);
    }
  }, [buildAuthHeaders, onAuthMismatch]);

  useEffect(() => {
    fetchAreaTypes();
    fetchPlans();
    fetchLicenses();
  }, [fetchAreaTypes, fetchPlans, fetchLicenses]);

  useEffect(() => {
    if (!licenses.length) return;
    licenses.forEach((license) => {
      const companyId = license.company?.id || license.companyId;
      if (companyId) {
        fetchUsageForCompany(companyId);
      }
    });
  }, [licenses, fetchUsageForCompany]);

  const openCreatePlanModal = () => {
    setEditingPlan(null);
    planForm.resetFields();
    planForm.setFieldsValue({
      isActive: true,
      sortOrder: 0,
      trialDurationDays: 7,
      areaTypeLimits: [],
      featureLightboxIntegration: true,
      featureMeasurementAnalysis: true,
      featureAreaAnalysis: true,
      featureEntityAnalysis: true,
    });
    setPlanModalVisible(true);
  };

  const openEditPlanModal = (plan: SubscriptionPlan) => {
    const resolvedFlags = resolveFeatureFlags(plan);
    setEditingPlan(plan);
    planForm.setFieldsValue({
      code: plan.code,
      name: plan.name,
      description: plan.description ?? null,
      isActive: plan.isActive,
      sortOrder: plan.sortOrder ?? 0,
      maxTotalUsers: plan.maxTotalUsers ?? null,
      maxAdminUsers: plan.maxAdminUsers ?? null,
      maxViewerUsers: plan.maxViewerUsers ?? null,
      maxStandardUsers: plan.maxStandardUsers ?? null,
      maxAreas: plan.maxAreas ?? null,
      maxEntities: plan.maxEntities ?? (typeof (plan.featureFlags as any)?.maxEntities === 'number'
        ? Number((plan.featureFlags as any).maxEntities)
        : null),
      entityAreaTypeIds: normalizeAreaTypeIdList(
        plan.entityAreaTypeIds ?? (plan.featureFlags as any)?.entityAreaTypeIds
      ) ?? [],
      areaTypeLimits: mapAreaTypeLimitsToFormRows(plan.areaTypeLimits),
      maxMeasurementsMonthly: plan.maxMeasurementsMonthly ?? null,
      maxAiTokensMonthly: plan.maxAiTokensMonthly ?? null,
      trialDurationDays: plan.trialDurationDays ?? (typeof (plan.featureFlags as any)?.trialDurationDays === 'number'
        ? Number((plan.featureFlags as any).trialDurationDays)
        : null),
      featureLightboxIntegration: resolvedFlags.lightboxIntegrationEnabled,
      featureMeasurementAnalysis: resolvedFlags.measurementAnalysisEnabled,
      featureAreaAnalysis: resolvedFlags.areaAnalysisEnabled,
      featureEntityAnalysis: resolvedFlags.entityAnalysisEnabled,
    });
    setPlanModalVisible(true);
  };

  const savePlan = async () => {
    try {
      const values = await planForm.validateFields();
      const payload = {
        code: values.code,
        name: values.name,
        description: values.description ?? null,
        isActive: values.isActive !== false,
        sortOrder: values.sortOrder ?? 0,
        maxTotalUsers: values.maxTotalUsers ?? null,
        maxAdminUsers: values.maxAdminUsers ?? null,
        maxViewerUsers: values.maxViewerUsers ?? null,
        maxStandardUsers: values.maxStandardUsers ?? null,
        maxAreas: values.maxAreas ?? null,
        areaTypeLimits: normalizeAreaTypeLimits(values.areaTypeLimits),
        maxEntities: values.maxEntities ?? null,
        entityAreaTypeIds: normalizeAreaTypeIdList(values.entityAreaTypeIds),
        maxMeasurementsMonthly: values.maxMeasurementsMonthly ?? null,
        maxAiTokensMonthly: values.maxAiTokensMonthly ?? null,
        trialDurationDays: values.trialDurationDays ?? null,
        requiresLightbox: typeof values.featureLightboxIntegration === 'boolean'
          ? values.featureLightboxIntegration
          : null,
        allowMultiUser: null,
        featureFlags: {
          genericAssistantEnabled: true,
          lightboxIntegrationEnabled: Boolean(values.featureLightboxIntegration),
          measurementAnalysisEnabled: Boolean(values.featureMeasurementAnalysis),
          specialistAgronomicAiEnabled: Boolean(values.featureMeasurementAnalysis),
          measurementLevelDetectionEnabled: Boolean(values.featureMeasurementAnalysis),
          areaAnalysisEnabled: Boolean(values.featureAreaAnalysis),
          entityAnalysisEnabled: Boolean(values.featureEntityAnalysis),
          crossAreaAnalysisEnabled: Boolean(values.featureEntityAnalysis),
          maxEntities: values.maxEntities ?? null,
          entityAreaTypeIds: normalizeAreaTypeIdList(values.entityAreaTypeIds),
          trialDurationDays: values.trialDurationDays ?? null,
        },
      };

      setSavingPlan(true);
      const endpoint = editingPlan
        ? `${API_BASE_URL}/subscription-plans/${editingPlan.id}`
        : `${API_BASE_URL}/subscription-plans`;
      const method = editingPlan ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        message.success(editingPlan ? 'Plan updated' : 'Plan created');
        setPlanModalVisible(false);
        setEditingPlan(null);
        await fetchPlans();
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.error || errorData?.message || 'Failed to save plan');
      }
    } catch (error) {
      console.error('Failed to save plan', error);
      message.error('Failed to save plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const deletePlan = async (planId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription-plans/${planId}`, {
        method: 'DELETE',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok || response.status === 204) {
        message.success('Plan deleted');
        await fetchPlans();
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.error || errorData?.message || 'Failed to delete plan');
      }
    } catch (error) {
      console.error('Failed to delete plan', error);
      message.error('Failed to delete plan');
    }
  };

  const openAssignModal = (license: CompanyLicense) => {
    setAssignmentTarget(license);
    assignForm.resetFields();
    assignForm.setFieldsValue({
      planCode: license.planCode || undefined,
      status: license.status || 'active',
      billingCycle: license.billingCycle || 'monthly',
      startsAt: license.startsAt ? dayjs(license.startsAt) : null,
      endsAt: license.endsAt ? dayjs(license.endsAt) : null,
    });
    setAssignModalVisible(true);
  };

  const saveAssignment = async () => {
    const companyId = getCompanyId(assignmentTarget || {});
    if (!companyId) return;
    try {
      const values = await assignForm.validateFields();
      const payload = {
        planCode: values.planCode,
        status: values.status,
        billingCycle: values.billingCycle || null,
        startsAt: values.startsAt ? values.startsAt.toISOString() : null,
        endsAt: values.endsAt ? values.endsAt.toISOString() : null,
      };

      setSavingAssignment(true);
      const response = await fetch(
        `${API_BASE_URL}/companies/${companyId}/license`,
        {
          method: 'PUT',
          headers: {
            ...buildAuthHeaders(),
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        message.success(hasAssignedLicense(assignmentTarget || {}) ? 'License updated' : 'License assigned');
        setAssignModalVisible(false);
        setAssignmentTarget(null);
        await fetchLicenses();
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.error || errorData?.message || 'Failed to assign subscription');
      }
    } catch (error) {
      console.error('Failed to assign subscription', error);
      message.error('Failed to assign subscription');
    } finally {
      setSavingAssignment(false);
    }
  };

  const planColumns: ColumnsType<SubscriptionPlan> = useMemo(() => [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (value) => <Text code>{value}</Text>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => (
        <Tag color={value ? 'green' : 'default'}>
          {value ? 'Active' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Limits',
      key: 'limits',
      render: (_, record) => {
        const trialDays = typeof record.trialDurationDays === 'number'
          ? record.trialDurationDays
          : (typeof (record.featureFlags as any)?.trialDurationDays === 'number'
            ? Number((record.featureFlags as any).trialDurationDays)
            : null);
        return (
          <Text type="secondary">
            Users {formatLimit(record.maxTotalUsers)} | Entities {formatLimit(record.maxEntities)} | Areas (total) {formatLimit(record.maxAreas)} | AI tokens {formatLimit(record.maxAiTokensMonthly)}{trialDays ? ` | Trial ${trialDays}d` : ''}
          </Text>
        );
      },
    },
    {
      title: 'Area Type Caps',
      key: 'areaTypeCaps',
      render: (_, record) => {
        if (!Array.isArray(record.areaTypeLimits) || record.areaTypeLimits.length === 0) {
          return <Text type="secondary">None</Text>;
        }

        return (
          <Space wrap size={[4, 4]}>
            {record.areaTypeLimits.map((limit) => (
              <Tag key={`${record.id}-${(limit.areaTypeIds || []).join('-')}-${limit.maxCount}`}>
                {areaTypeLimitSummary(limit, areaTypeNameById)}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Flags',
      key: 'flags',
      render: (_, record) => {
        const flags = resolveFeatureFlags(record);
        return (
          <Space size={4} wrap>
            <Tag color="geekblue">Generic AI</Tag>
            <Tag color={flags.lightboxIntegrationEnabled ? 'blue' : 'default'}>
              Lightbox {flags.lightboxIntegrationEnabled ? 'On' : 'Off'}
            </Tag>
            <Tag color={flags.measurementAnalysisEnabled ? 'green' : 'default'}>
              Measurement AI {flags.measurementAnalysisEnabled ? 'On' : 'Off'}
            </Tag>
            <Tag color={flags.areaAnalysisEnabled ? 'green' : 'default'}>
              Area AI {flags.areaAnalysisEnabled ? 'On' : 'Off'}
            </Tag>
            <Tag color={flags.entityAnalysisEnabled ? 'green' : 'default'}>
              Entity/Cross-area AI {flags.entityAnalysisEnabled ? 'On' : 'Off'}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEditPlanModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title={`Delete plan "${record.code}"?`}
            onConfirm={() => deletePlan(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], [areaTypeNameById]);

  const assignmentColumns: ColumnsType<CompanyLicense> = useMemo(() => [
    {
      title: 'Subscription Owner',
      key: 'owner',
      render: (_, record) => record.creatorUser?.email || <Text type="secondary">No creator linked</Text>,
    },
    {
      title: 'Company ID',
      key: 'companyId',
      render: (_, record) => {
        const companyId = getCompanyId(record);
        return companyId ? <Text code>{companyId}</Text> : <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Company',
      key: 'company',
      render: (_, record) => getCompanyName(record),
    },
    {
      title: 'Plan',
      key: 'planCode',
      render: (_, record) => (
        hasAssignedLicense(record)
          ? <Tag>{record.planCode || 'default'}</Tag>
          : <Tag color="default">No license</Tag>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        if (!hasAssignedLicense(record)) {
          return <Tag color="default">Unassigned</Tag>;
        }
        const value = record.status;
        return (
          <Tag color={value === 'active' ? 'green' : 'gold'}>
            {value || 'active'}
          </Tag>
        );
      },
    },
    {
      title: 'Cycle',
      key: 'billingCycle',
      render: (_, record) => (
        hasAssignedLicense(record) && record.billingCycle
          ? <Tag>{record.billingCycle}</Tag>
          : <Text type="secondary">-</Text>
      ),
    },
    {
      title: 'Users',
      key: 'users',
      render: (_, record) => {
        const companyId = getCompanyId(record);
        const usage = companyId ? usageByCompany[companyId] : undefined;
        if (!hasAssignedLicense(record)) {
          return <Tag color="default">{usage ? `${usage.totalUsers}/No license` : 'No license'}</Tag>;
        }
        const label = usage ? `${usage.totalUsers}/${formatLimit(record.maxTotalUsers)}` : '--';
        const isOver = typeof record.maxTotalUsers === 'number' && !!usage && usage.totalUsers >= record.maxTotalUsers;
        const breakdown = usage
          ? [
              `Admins: ${usage.adminUsers}/${formatLimit(record.maxAdminUsers)}`,
              `Viewers: ${usage.viewerUsers}/${formatLimit(record.maxViewerUsers)}`,
              `Standard: ${usage.standardUsers}/${formatLimit(record.maxStandardUsers)}`,
            ].join(' | ')
          : '';

        return (
          <Tooltip title={breakdown}>
            <Tag color={isOver ? 'red' : 'blue'}>{label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Areas',
      key: 'areas',
      render: (_, record) => {
        const companyId = getCompanyId(record);
        const usage = companyId ? usageByCompany[companyId] : undefined;
        if (!hasAssignedLicense(record)) {
          return <Tag color="default">{usage ? `${usage.areas}/No license` : 'No license'}</Tag>;
        }
        const label = usage ? `${usage.areas}/${formatLimit(record.maxAreas)}` : '--';
        const isOver = typeof record.maxAreas === 'number' && !!usage && usage.areas >= record.maxAreas;
        return <Tag color={isOver ? 'red' : 'blue'}>{label}</Tag>;
      },
    },
    {
      title: 'Measurements (month)',
      key: 'measurements',
      render: (_, record) => {
        const companyId = getCompanyId(record);
        const usage = companyId ? usageByCompany[companyId] : undefined;
        if (!hasAssignedLicense(record)) {
          return <Tag color="default">{usage ? `${usage.measurementsMonthly}/No license` : 'No license'}</Tag>;
        }
        const label = usage ? `${usage.measurementsMonthly}/${formatLimit(record.maxMeasurementsMonthly)}` : '--';
        const isOver = typeof record.maxMeasurementsMonthly === 'number' && !!usage && usage.measurementsMonthly >= record.maxMeasurementsMonthly;
        return <Tag color={isOver ? 'red' : 'blue'}>{label}</Tag>;
      },
    },
    {
      title: 'AI Tokens (month)',
      key: 'aiTokens',
      render: (_, record) => {
        const companyId = getCompanyId(record);
        const usage = companyId ? usageByCompany[companyId] : undefined;
        if (!hasAssignedLicense(record)) {
          return <Tag color="default">{usage ? `${usage.aiTokensMonthly}/No license` : 'No license'}</Tag>;
        }
        const label = usage ? `${usage.aiTokensMonthly}/${formatLimit(record.maxAiTokensMonthly)}` : '--';
        const isOver = typeof record.maxAiTokensMonthly === 'number' && !!usage && usage.aiTokensMonthly >= record.maxAiTokensMonthly;
        return <Tag color={isOver ? 'red' : 'blue'}>{label}</Tag>;
      },
    },
    {
      title: 'Warnings',
      key: 'warnings',
      render: (_, record) => {
        const companyId = getCompanyId(record);
        const warnings = companyId ? warningsByCompany[companyId] : [];
        if (!hasAssignedLicense(record) && (!warnings || warnings.length === 0)) {
          return <Tag color="gold">No license</Tag>;
        }
        if (!warnings || warnings.length === 0) {
          return <Tag color="default">None</Tag>;
        }
        return (
          <Tooltip
            title={
              <Space direction="vertical">
                {warnings.map((warning) => (
                  <Text key={warning.key} style={{ color: '#fff' }}>
                    {warning.message}
                  </Text>
                ))}
              </Space>
            }
          >
            <Tag color="red">{warnings.length} alert{warnings.length > 1 ? 's' : ''}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" onClick={() => openAssignModal(record)}>
          {hasAssignedLicense(record) ? 'Edit license' : 'Add license'}
        </Button>
      ),
    },
  ], [usageByCompany, warningsByCompany]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        title="Subscription Plans"
        extra={<Button type="primary" onClick={openCreatePlanModal}>Create plan</Button>}
        loading={loadingPlans}
      >
        <Text type="secondary">
          Define plans and limits centrally. Plans can then be assigned directly to any company.
        </Text>
        <Table
          style={{ marginTop: 12 }}
          rowKey="id"
          columns={planColumns}
          dataSource={plans}
          pagination={{ pageSize: 6 }}
        />
      </Card>

      <Card title="Company Subscriptions" loading={loadingLicenses}>
        <Text type="secondary">
          Every company is listed here. Companies without a license appear as unassigned and can be provisioned directly from this table.
        </Text>
        <Table
          style={{ marginTop: 12 }}
          rowKey={(record) => getCompanyId(record) || `company-license-${record.id || 'unknown'}`}
          columns={assignmentColumns}
          dataSource={licenses}
          pagination={{ pageSize: 8 }}
        />
      </Card>

      <Modal
        title={editingPlan ? `Edit plan: ${editingPlan.code}` : 'Create subscription plan'}
        open={planModalVisible}
        onCancel={() => setPlanModalVisible(false)}
        onOk={savePlan}
        confirmLoading={savingPlan}
        okText="Save"
        width={980}
      >
        <Form form={planForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: 'Code is required' }]}
              >
                <Input placeholder="basic" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <Input placeholder="OSCAR BASIC" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Description" name="description">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Status" name="isActive">
                <Select>
                  <Select.Option value={true}>Active</Select.Option>
                  <Select.Option value={false}>Disabled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Sort order" name="sortOrder">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Max total users" name="maxTotalUsers">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Max areas (total)" name="maxAreas">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Max entities (total)"
                name="maxEntities"
                tooltip="Top-level entities counted across selected entity area types."
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Entity area types"
                name="entityAreaTypeIds"
                tooltip="Defines which area types are treated as entities for max entities and entity-level AI."
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select entity area types"
                  options={areaTypes.map((areaType) => ({
                    value: areaType.id,
                    label: areaType.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Max admin users" name="maxAdminUsers">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Max viewer users" name="maxViewerUsers">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Max standard users" name="maxStandardUsers">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Max measurements / month" name="maxMeasurementsMonthly">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Max AI tokens / month" name="maxAiTokensMonthly">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Trial duration (days)"
                name="trialDurationDays"
                tooltip="Used when assigning this plan with status=trial and no explicit end date."
              >
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Lightbox integration"
                name="featureLightboxIntegration"
                rules={[{ required: true, message: 'Select if Lightbox integration is enabled' }]}
                tooltip="If disabled, Lightbox sign-in/integration is blocked. Web login is not blocked."
              >
                <Select>
                  <Select.Option value={true}>Enabled</Select.Option>
                  <Select.Option value={false}>Disabled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Measurement analysis AI"
                name="featureMeasurementAnalysis"
                rules={[{ required: true, message: 'Select if measurement analysis AI is enabled' }]}
                tooltip="Includes specialist agronomic AI + measurement-level detection/identification."
              >
                <Select>
                  <Select.Option value={true}>Enabled</Select.Option>
                  <Select.Option value={false}>Disabled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Area analysis AI"
                name="featureAreaAnalysis"
                rules={[{ required: true, message: 'Select if area analysis AI is enabled' }]}
                tooltip="Controls area-level AI analysis flows."
              >
                <Select>
                  <Select.Option value={true}>Enabled</Select.Option>
                  <Select.Option value={false}>Disabled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Entity/Cross-area AI"
                name="featureEntityAnalysis"
                rules={[{ required: true, message: 'Select if entity/cross-area AI is enabled' }]}
                tooltip="Entity-level AI and cross-area AI share the same switch."
              >
                <Select>
                  <Select.Option value={true}>Enabled</Select.Option>
                  <Select.Option value={false}>Disabled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
            Generic AI assistant is always enabled for all plans.
          </Text>

          <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
            Total area limit across the company. Area-type group caps below are additional constraints.
          </Text>
          <Form.List name="areaTypeLimits">
            {(fields, { add, remove }) => (
              <div style={{ marginBottom: 12 }}>
                <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 8 }}>
                  <Text strong>Area type group caps</Text>
                  <Button type="link" onClick={() => add({ areaTypeIds: [], maxCount: undefined })}>
                    Add area type group
                  </Button>
                </Space>
                <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                  Select multiple area types in one row to apply one shared cap across that whole group.
                </Text>
                {fields.map((field) => (
                  <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="start">
                    <Form.Item
                      {...field}
                      name={[field.name, 'areaTypeIds']}
                      rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one area type' }]}
                    >
                      <Select
                        mode="multiple"
                        style={{ width: 360 }}
                        showSearch
                        optionFilterProp="label"
                        placeholder="Select one or more area types"
                        options={areaTypes.map((areaType) => ({
                          value: areaType.id,
                          label: areaType.name,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'maxCount']}
                      rules={[{ required: true, message: 'Set max count' }]}
                    >
                      <InputNumber min={0} placeholder="Max count" />
                    </Form.Item>
                    <Button danger onClick={() => remove(field.name)}>
                      Remove
                    </Button>
                  </Space>
                ))}
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal
        title={`${hasAssignedLicense(assignmentTarget || {}) ? 'Edit license' : 'Add license'}${assignmentTarget ? `: ${getCompanyName(assignmentTarget)}${getCompanyId(assignmentTarget) ? ` (#${getCompanyId(assignmentTarget)})` : ''}` : ''}`}
        open={assignModalVisible}
        onCancel={() => setAssignModalVisible(false)}
        onOk={saveAssignment}
        confirmLoading={savingAssignment}
        okText={hasAssignedLicense(assignmentTarget || {}) ? 'Save' : 'Assign'}
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item
            label="Plan"
            name="planCode"
            rules={[{ required: true, message: 'Plan is required' }]}
          >
            <Select placeholder="Select a plan">
              {plans
                .filter((plan) => plan.isActive)
                .map((plan) => (
                  <Select.Option key={plan.code} value={plan.code}>
                    {plan.name} ({plan.code})
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item label="License status" name="status">
            <Select>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="trial">Trial</Select.Option>
              <Select.Option value="past_due">Past due</Select.Option>
              <Select.Option value="suspended">Suspended</Select.Option>
              <Select.Option value="canceled">Canceled</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="Billing cycle"
            name="billingCycle"
            tooltip="Used to auto-calculate end date when no explicit end date is provided."
          >
            <Select allowClear placeholder="Select cycle">
              <Select.Option value="monthly">Monthly</Select.Option>
              <Select.Option value="annual">Annual</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Starts at" name="startsAt">
            <DatePicker style={{ width: '100%' }} showTime />
          </Form.Item>
          <Form.Item label="Ends at" name="endsAt">
            <DatePicker style={{ width: '100%' }} showTime />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default CompanyUsageTab;
